import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Warehouse, ShieldCheck, Inbox, BadgeCheck, MapPin, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { AccessGuide } from "@/components/shared/AccessGuide";
import { KpiCard } from "@/components/shared/KpiCard";
import { AuditList } from "@/components/supplier/AuditList";
import { ReviewQueue } from "@/components/supplier/ReviewQueue";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";

const TODAY = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function SupplierHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [inReview, setInReview] = useState(null);
  const { user, memberships } = useAuth();
  const orgName = memberships.find((m) => m.org_type === "SUPPLIER")?.organisation_name;
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/supplier").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <ErrorState testId="supplier-dashboard-error" />;
  if (!data) return <LoadingState testId="supplier-dashboard-loading" />;

  const k = data.kpis;

  return (
    <div data-testid="supplier-home-page" className="space-y-6">
      <div data-testid="brand-banner" className="flex items-center gap-3 rounded-xl bg-jigsaw-navy px-5 py-3.5">
        <img src="/brand/jigsaw-icon.png" alt="Jigsaw" className="h-8 w-8 shrink-0 object-contain" />
        <p className="text-sm text-white/80">
          <span className="font-semibold text-white">Jigsaw Supplier Portal</span> — keep your properties and compliance evidence up to date for {orgName}.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Welcome, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-sm text-jigsaw-muted">{TODAY}</p>
        </div>
        {data.supplier && <StatusBadge status={data.supplier.status} />}
      </div>

      {!data.supplier ? (
        <EmptyState testId="supplier-no-profile" icon={Warehouse} title="No supplier profile linked" description={data.message} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard testId="kpi-properties" label="Properties" value={k.properties} icon={Warehouse} onClick={() => navigate("/supplier/properties")} />
            <KpiCard testId="kpi-pending-compliance" label="Pending Compliance" value={k.pending_compliance} icon={ShieldCheck} tone={k.pending_compliance > 0 ? "danger" : "default"} onClick={() => navigate("/supplier/compliance")} />
            <KpiCard testId="kpi-open-requests" label="Open Requests from Jigsaw" value={k.open_requests_from_jigsaw} icon={Inbox} onClick={() => navigate("/supplier/requests")} />
            <KpiCard testId="kpi-submissions-in-review" label="Submissions in review" value={inReview == null ? "—" : inReview} icon={ClipboardList} onClick={() => navigate("/supplier/submissions")} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <Card data-testid="supplier-open-requests-panel" className="border-jigsaw-border">
              <CardHeader><CardTitle className="text-base text-jigsaw-navy">Requests from Jigsaw operations</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.open_requests_list.length === 0 && <p className="text-sm text-jigsaw-muted">No open requests right now.</p>}
                {data.open_requests_list.map((r) => (
                  <article key={r.id} data-testid={`supplier-open-request-${r.jigsaw_reference}`} className="rounded-[10px] border border-jigsaw-border px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-ref rounded-md bg-jigsaw-navy px-2 py-0.5 text-[12px] font-semibold text-white">{r.jigsaw_reference}</span>
                      <span className="text-[12px] text-jigsaw-muted">Arrival {r.arrival_date ? new Date(r.arrival_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <div><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Requirement</dt><dd className="text-[14px] text-jigsaw-navy">{r.rooms_required} room(s) · {r.guests_count} guest(s)</dd></div>
                      <div><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Location</dt><dd className="flex items-center gap-1 truncate text-[14px] text-jigsaw-navy"><MapPin className="h-3.5 w-3.5 shrink-0 text-jigsaw-orange" />{r.location_text || "—"}</dd></div>
                      <div className="col-span-2 sm:col-span-1"><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Respond</dt><dd><Link data-testid={`supplier-open-request-link-${r.jigsaw_reference}`} to="/supplier/requests" className="inline-flex min-h-[32px] items-center text-[14px] text-jigsaw-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/30">View requests →</Link></dd></div>
                    </dl>
                  </article>
                ))}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <ReviewQueue supplierId={data.supplier.id} onLoaded={(items) => setInReview(items.filter((s) => s.status === "SUBMITTED").length)} />
              <AuditList endpoint="/supplier/activity" title="Audit stream" subtitle="Changes recorded on your profile, properties, documents and submissions" testId="supplier-activity" emptyText="Nothing has changed on your records yet." />
            </div>
          </div>

          <Card data-testid="supplier-quick-links-panel" className="border-jigsaw-border">
            <CardHeader><CardTitle className="text-base text-jigsaw-navy">Quick actions</CardTitle></CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-3">
              <button data-testid="supplier-quick-link-properties" onClick={() => navigate("/supplier/properties")} className="flex items-center gap-2.5 rounded-md border border-jigsaw-border px-3 py-2.5 text-left text-sm hover:border-jigsaw-orange/40 hover:bg-jigsaw-orange/5">
                <Warehouse className="h-4 w-4 text-jigsaw-orange" /> Manage your properties
              </button>
              <button data-testid="supplier-quick-link-compliance" onClick={() => navigate("/supplier/compliance")} className="flex items-center gap-2.5 rounded-md border border-jigsaw-border px-3 py-2.5 text-left text-sm hover:border-jigsaw-orange/40 hover:bg-jigsaw-orange/5">
                <BadgeCheck className="h-4 w-4 text-jigsaw-orange" /> Upload compliance evidence
              </button>
              <button data-testid="supplier-quick-link-requests" onClick={() => navigate("/supplier/requests")} className="flex items-center gap-2.5 rounded-md border border-jigsaw-border px-3 py-2.5 text-left text-sm hover:border-jigsaw-orange/40 hover:bg-jigsaw-orange/5">
                <Inbox className="h-4 w-4 text-jigsaw-orange" /> Respond to Jigsaw's requests
              </button>
            </CardContent>
          </Card>
        </>
      )}
      <AccessGuide workspace="SUPPLIER" />
    </div>
  );
}
